import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { filter } from "lodash";

import { BuyerDashboardItem } from "containers/BuyerDashboard/assetAdministrationsPostProcessor";

export interface FilterOption {
  label: string;
  key: keyof BuyerDashboardItem;
  value: any[];
}

const useFilteredBuyerDashboardItems = (
  buyerDashboardItems: BuyerDashboardItem[],
): [
  BuyerDashboardItem[],
  FilterOption[],
  Dispatch<SetStateAction<FilterOption[]>>,
  (clickedOption: FilterOption) => void,
] => {
  const [activeFilterOptions, setActiveFilterOptions] = useState<
    FilterOption[]
  >([]);

  const [
    filteredBuyerDashboardItems,
    setFilteredBuyerDashboardItems,
  ] = useState(buyerDashboardItems);

  const handleFilterChange = (clickedOption: FilterOption) => {
    setActiveFilterOptions(prev => {
      const prevOtherKindOptions = prev.filter(
        option => option.key !== clickedOption.key,
      );
      const prevClickedKindOptions = prev.filter(
        option => option.key === clickedOption.key,
      );

      if (prevClickedKindOptions.includes(clickedOption)) {
        // un-selecting the option
        return [
          ...prevOtherKindOptions,
          ...prevClickedKindOptions.filter(
            option => option.label !== clickedOption.label,
          ),
        ];
      } else {
        // selecting the option
        return [
          ...prevOtherKindOptions,
          ...prevClickedKindOptions,
          clickedOption,
        ];
      }
    });
  };

  useEffect(() => {
    const filterObj: Record<
      keyof BuyerDashboardItem,
      boolean[]
    > = activeFilterOptions.reduce((acc, currentFilterOption) => {
      const updatedAcc = { ...acc };
      if (currentFilterOption.key in updatedAcc) {
        updatedAcc[currentFilterOption.key] = [
          ...updatedAcc[currentFilterOption.key],
          ...currentFilterOption.value,
        ];
      } else {
        updatedAcc[currentFilterOption.key] = currentFilterOption.value;
      }
      return updatedAcc;
    }, {} as Record<keyof BuyerDashboardItem, boolean[]>);

    const filterPredicate = (item: BuyerDashboardItem) => {
      for (const [key, value] of Object.entries(filterObj)) {
        const keyTyped = key as keyof BuyerDashboardItem;
        const itemProp = item[keyTyped] as boolean;
        if (!value.includes(itemProp)) {
          return false;
        }
      }
      return true;
    };
    setFilteredBuyerDashboardItems(
      filter(buyerDashboardItems, filterPredicate),
    );
  }, [activeFilterOptions, buyerDashboardItems]);
  return [
    filteredBuyerDashboardItems,
    activeFilterOptions,
    setActiveFilterOptions,
    handleFilterChange,
  ];
};

export default useFilteredBuyerDashboardItems;
